import { useState } from 'react'
import { PiCaretDown, PiPlus, PiX } from 'react-icons/pi'
import { primaryPillButtonClass } from 'components/FbUI/secondaryPillButton'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { cn } from 'components/UI/cn'
import { frameOf } from '../frames'
import {
  dateFilterFields,
  dateFilterOf,
  hasOpenFilter,
  inFilterFields,
  inFilterValues,
  numericFilterFields,
  numericFilterOf,
  removeDateFilter,
  removeInFilter,
  removeNumericFilter,
  setDateFilter,
  setInFilter,
  setNumericFilter,
  setOpenFilter,
} from '../pipeline-ops'
import { Filter, ViewConfig } from '../view-config'
import { MenuItem, MenuSection } from './FilterMenu'
import { MatchedSelect } from './MatchedSelect'
import { MultiSelectList } from './MultiSelectList'
import { fieldLabelOf } from './ReportTable'
import {
  DateCompareFilterRow,
  NumericFilterRow,
  OpenDealsRow,
} from './ScalarFilters'

/**
 * Session-layer filters + sort the user lays on top of a *saved* list report
 * while viewing it — "deals in stage X for >30 days, newest first" — without
 * entering the full editor. These never touch the saved report's own (base)
 * filters: they're held separately by ReportDetailPage and merged into the
 * query (engine ANDs all filters; list sort runs before the row cap).
 *
 * The conditions row (Notion-style): each active filter is a compact **pill**
 * showing a summary — click it to edit in a popover, or hit its × to remove.
 * `+ Filter` adds a field (landing with a default), `Sort` sets the order, and
 * Reset / "Save as report" sit on the right. The header Filter/Sort toggles
 * (in ReportDetailPage) show/hide this whole row; it's mounted only when open.
 */

/** The quick-explore overlay: extra filters + an optional sort override. */
export type QuickState = {
  filters: Filter[]
  sort?: { by: string; dir: 'asc' | 'desc' }
}

const NO_SORT = '__default__'

/** A pill in the bar — one active quick filter, tagged by editor kind. */
type Pill =
  | { kind: 'cat'; field: string }
  | { kind: 'num'; field: string }
  | { kind: 'date'; field: string }
  | { kind: 'open' }

/** Stable popover key per pill (drives which editor is open). */
const pillKey = (p: Pill): string =>
  p.kind === 'open' ? 'open' : `${p.kind}:${p.field}`

export function QuickFilterBar({
  config,
  quick,
  onChange,
  onSaveAsReport,
}: {
  config: ViewConfig
  quick: QuickState
  onChange: (next: QuickState) => void
  /** Persist the current base + quick layer as a new saved report. */
  onSaveAsReport?: () => void
}) {
  const frame = frameOf(config.frame)
  const [addOpen, setAddOpen] = useState(false)
  // Two-step "+ Filter" popover: null → show the field menu; a Pill → show that
  // just-added field's value editor in the SAME popover (so picking a field and
  // choosing its values is one flow, no second-popover dismiss race).
  const [addStep, setAddStep] = useState<Pill | null>(null)
  // Which pill's editor popover is open (its pillKey), or null. Pills open their
  // editor on click; a freshly added pill lands closed (see `add`).
  const [openKey, setOpenKey] = useState<string | null>(null)
  // Synthetic config so the pipeline-ops helpers operate on the quick layer
  // alone — removing a quick filter can never strip a base one.
  const qc: ViewConfig = { ...config, filters: quick.filters }
  const fieldLabel = (f: string): string => fieldLabelOf(frame, f)
  const distinctValues = (f: string): string[] =>
    (frame.fields.find((x) => x.name === f)?.values ?? []).map(String).sort()
  const apply = (next: ViewConfig) =>
    onChange({ ...quick, filters: next.filters })
  // Pick a field in the add menu: apply its (default) filter and advance to the
  // value editor within the same popover — no flash, values are chosen next.
  const pickField = (next: ViewConfig, p: Pill) => {
    apply(next)
    setAddStep(p)
  }
  // Close the add popover and reset its step.
  const finishAdd = () => {
    setAddOpen(false)
    setAddStep(null)
  }
  // Remove a filter and close any open editor (pill popover or add step).
  const remove = (next: ViewConfig) => {
    apply(next)
    setOpenKey(null)
    finishAdd()
  }
  // The config with pill `p`'s filter removed (drives the pill's × and the
  // editor's Remove).
  const removeFilterFor = (p: Pill): ViewConfig =>
    p.kind === 'cat'
      ? removeInFilter(qc, p.field)
      : p.kind === 'num'
        ? removeNumericFilter(qc, p.field)
        : p.kind === 'date'
          ? removeDateFilter(qc, p.field)
          : setOpenFilter(qc, false)

  const pills: Pill[] = [
    ...inFilterFields(qc).map((field): Pill => ({ kind: 'cat', field })),
    ...numericFilterFields(qc).map((field): Pill => ({ kind: 'num', field })),
    ...dateFilterFields(qc).map((field): Pill => ({ kind: 'date', field })),
    ...(hasOpenFilter(qc) ? [{ kind: 'open' } as Pill] : []),
  ]

  // A field is "taken" if either the saved report or the quick layer already
  // filters it — don't offer a second, confusing filter on the same column.
  const taken = new Set<string>([
    ...config.filters.flatMap((f) => ('field' in f ? [f.field] : [])),
    ...quick.filters.flatMap((f) => ('field' in f ? [f.field] : [])),
  ])
  const unusedCat = frame.catDims.filter((f) => !taken.has(f))
  const unusedNum = frame.numFields.filter((f) => !taken.has(f))
  const unusedDate = frame.dateFields.filter((f) => !taken.has(f))
  const hasCloseDate = frame.fields.some((f) => f.name === 'datetime_closed')
  const canAddOpen =
    hasCloseDate && !hasOpenFilter(config) && !hasOpenFilter(qc)
  const canAdd =
    unusedCat.length > 0 ||
    unusedNum.length > 0 ||
    unusedDate.length > 0 ||
    canAddOpen

  const isDirty = quick.filters.length > 0 || !!quick.sort
  const sortDir = quick.sort?.dir ?? 'desc'

  // ── Pill summary text ──────────────────────────────────────────────────────
  const catSummary = (field: string): string => {
    const vals = inFilterValues(qc, field)
    const count = distinctValues(field).length
    const text =
      vals.length === 0
        ? 'any'
        : vals.length === count
          ? 'all'
          : vals.length <= 2
            ? vals.join(', ')
            : `${vals.length} selected`
    return `${fieldLabel(field)}: ${text}`
  }
  const numSummary = (field: string): string => {
    const cond = numericFilterOf(qc, field)
    const sym = cond?.op === 'lte' ? '≤' : '≥'
    return `${fieldLabel(field)} ${sym} ${cond?.value ?? 0}`
  }
  const dateSummary = (field: string): string => {
    const cond = dateFilterOf(qc, field)
    if (cond?.op === 'within_days')
      return `${fieldLabel(field)} ≤ ${cond.value}d`
    const sym = cond?.op === 'date_before' ? '≤' : '≥'
    return `${fieldLabel(field)} ${sym} ${cond?.value ?? ''}`
  }
  const pillLabel = (p: Pill): string =>
    p.kind === 'cat'
      ? catSummary(p.field)
      : p.kind === 'num'
        ? numSummary(p.field)
        : p.kind === 'date'
          ? dateSummary(p.field)
          : 'Open deals'

  // ── Pill editor (popover body) ───────────────────────────────────────────
  const pillEditor = (p: Pill): React.ReactNode => {
    if (p.kind === 'cat') {
      return (
        <>
          <MultiSelectList
            label={fieldLabel(p.field)}
            idPrefix={`quick-filter-${p.field}`}
            options={distinctValues(p.field).map((v) => ({
              value: v,
              label: v,
            }))}
            selected={inFilterValues(qc, p.field)}
            onChange={(next) => apply(setInFilter(qc, p.field, next))}
          />
          <div className="border-t border-line p-1">
            <button
              type="button"
              onClick={() => remove(removeInFilter(qc, p.field))}
              className="flex w-full rounded px-2 py-1.5 text-left text-sm text-fg-muted hover:bg-surface-hover hover:text-error"
            >
              Remove filter
            </button>
          </div>
        </>
      )
    }
    if (p.kind === 'num') {
      const cond = numericFilterOf(qc, p.field)
      return (
        <div className="p-3">
          <NumericFilterRow
            label={fieldLabel(p.field)}
            ariaLabel={fieldLabel(p.field)}
            op={cond?.op ?? 'gte'}
            value={cond?.value ?? 0}
            onChange={(op, value) =>
              apply(setNumericFilter(qc, p.field, op, value))
            }
            onRemove={() => remove(removeNumericFilter(qc, p.field))}
          />
        </div>
      )
    }
    if (p.kind === 'date') {
      const cond = dateFilterOf(qc, p.field)
      return (
        <div className="p-3">
          <DateCompareFilterRow
            label={fieldLabel(p.field)}
            ariaLabel={fieldLabel(p.field)}
            op={cond?.op ?? 'date_after'}
            value={cond?.value ?? ''}
            onChange={(op, value) =>
              apply(setDateFilter(qc, p.field, op, value))
            }
            onRemove={() => remove(removeDateFilter(qc, p.field))}
          />
        </div>
      )
    }
    return (
      <div className="p-3">
        <OpenDealsRow onRemove={() => remove(setOpenFilter(qc, false))} />
      </div>
    )
  }

  const pillClass =
    'inline-flex h-8 items-center rounded-full border border-line bg-surface-sunken text-sm text-fg transition-colors hover:border-brand-active'
  // Notion-style subtle text buttons for "+ Filter" and "Sort" inside the row.
  const ghostButtonClass =
    'inline-flex h-8 items-center gap-1 rounded-md px-2 text-sm text-fg-secondary transition-colors hover:bg-surface-hover hover:text-fg'

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface p-3">
      {pills.map((p) => {
        const key = pillKey(p)
        return (
          <div key={key} className={pillClass}>
            {/* Click the label to edit; the trailing × removes the filter. The
                × is a sibling (not nested in the trigger button). */}
            <Popover
              open={openKey === key}
              onOpenChange={(o) => setOpenKey(o ? key : null)}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-full items-center gap-1.5 rounded-l-full pl-3 pr-1.5"
                >
                  <span className="max-w-[18rem] truncate">{pillLabel(p)}</span>
                  <PiCaretDown size={12} className="shrink-0 text-fg-muted" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-0">
                {pillEditor(p)}
              </PopoverContent>
            </Popover>
            <button
              type="button"
              aria-label={`Remove ${pillLabel(p)}`}
              onClick={() => remove(removeFilterFor(p))}
              className="inline-flex h-full items-center rounded-r-full pr-2 pl-0.5 text-fg-muted hover:text-error"
            >
              <PiX size={12} />
            </button>
          </div>
        )
      })}

      {/* + Filter and Sort flow after the pills (Notion-style); Reset / Save
          sit on the right. The header funnel/sort icons toggle this row. */}
      {canAdd && (
        <Popover
          open={addOpen}
          onOpenChange={(o) => {
            setAddOpen(o)
            if (!o) setAddStep(null)
          }}
        >
          <PopoverTrigger asChild>
            <button type="button" className={ghostButtonClass}>
              <PiPlus size={14} /> Filter
            </button>
          </PopoverTrigger>
          {addStep ? (
            // Step 2: value editor for the just-picked field, in place.
            <PopoverContent align="start" className="w-64 p-0">
              {pillEditor(addStep)}
              <div className="border-t border-line p-1">
                <button
                  type="button"
                  onClick={finishAdd}
                  className="flex w-full justify-center rounded px-2 py-1.5 text-sm font-medium text-brand-active hover:bg-surface-hover"
                >
                  Done
                </button>
              </div>
            </PopoverContent>
          ) : (
            // Step 1: pick a field. Categories seed empty so the user chooses
            // values next (in step 2); numbers/dates seed an editable default.
            <PopoverContent align="start" className="w-56 p-1">
              {unusedCat.length > 0 && (
                <MenuSection label="Categories">
                  {unusedCat.map((f) => (
                    <MenuItem
                      key={f}
                      label={fieldLabel(f)}
                      onClick={() =>
                        pickField(setInFilter(qc, f, []), {
                          kind: 'cat',
                          field: f,
                        })
                      }
                    />
                  ))}
                </MenuSection>
              )}
              {unusedNum.length > 0 && (
                <MenuSection label="Numbers">
                  {unusedNum.map((f) => (
                    <MenuItem
                      key={f}
                      label={fieldLabel(f)}
                      onClick={() =>
                        pickField(setNumericFilter(qc, f, 'gte', 0), {
                          kind: 'num',
                          field: f,
                        })
                      }
                    />
                  ))}
                </MenuSection>
              )}
              {unusedDate.length > 0 && (
                <MenuSection label="Dates">
                  {unusedDate.map((f) => (
                    <MenuItem
                      key={f}
                      label={fieldLabel(f)}
                      onClick={() =>
                        pickField(
                          setDateFilter(
                            qc,
                            f,
                            'date_after',
                            `${new Date().getFullYear()}-01-01`
                          ),
                          { kind: 'date', field: f }
                        )
                      }
                    />
                  ))}
                </MenuSection>
              )}
              {canAddOpen && (
                <MenuSection label="Status">
                  <MenuItem
                    label="Open deals only"
                    onClick={() => {
                      apply(setOpenFilter(qc, true))
                      finishAdd()
                    }}
                  />
                </MenuSection>
              )}
            </PopoverContent>
          )}
        </Popover>
      )}

      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={ghostButtonClass}>
            {quick.sort ? (
              <span className="text-fg">
                Sort: {fieldLabel(quick.sort.by)}{' '}
                {sortDir === 'asc' ? '↑' : '↓'}
              </span>
            ) : (
              'Sort'
            )}
            <PiCaretDown size={12} className="shrink-0 text-fg-muted" />
          </button>
        </PopoverTrigger>
        {/* Sort runs before the row cap, so it's a real "top N by …", not a
            reshuffle of the shown rows. Default = the saved report's order. */}
        <PopoverContent align="start" className="flex w-56 flex-col gap-2 p-3">
          <div className="flex flex-col">
            <span className="mb-1 text-xs text-fg-secondary">Sort by</span>
            <MatchedSelect
              ariaLabel="Quick sort field"
              options={[
                { value: NO_SORT, label: 'Default order' },
                ...frame.columns.map((f) => ({
                  value: f,
                  label: fieldLabel(f),
                })),
              ]}
              value={{
                value: quick.sort?.by ?? NO_SORT,
                label: quick.sort ? fieldLabel(quick.sort.by) : 'Default order',
              }}
              onChange={(v) =>
                onChange({
                  ...quick,
                  sort: v === NO_SORT ? undefined : { by: v, dir: sortDir },
                })
              }
            />
          </div>
          {quick.sort && (
            <div className="flex flex-col">
              <span className="mb-1 text-xs text-fg-secondary">Order</span>
              <MatchedSelect
                ariaLabel="Quick sort order"
                options={[
                  { value: 'desc', label: 'High → low' },
                  { value: 'asc', label: 'Low → high' },
                ]}
                value={{
                  value: sortDir,
                  label: sortDir === 'asc' ? 'Low → high' : 'High → low',
                }}
                onChange={(dir) =>
                  onChange({
                    ...quick,
                    sort: { by: quick.sort!.by, dir: dir as 'asc' | 'desc' },
                  })
                }
              />
            </div>
          )}
        </PopoverContent>
      </Popover>

      {isDirty && (
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              onChange({ filters: [] })
              setOpenKey(null)
            }}
            className="px-1 text-xs text-fg-secondary hover:text-fg"
          >
            Reset
          </button>
          {onSaveAsReport && (
            <button
              type="button"
              onClick={onSaveAsReport}
              className={cn(primaryPillButtonClass, 'h-8')}
            >
              Save as report
            </button>
          )}
        </div>
      )}
    </div>
  )
}
